/**
 * Roadmap #146 Alt-PR C+D — sticky bottom QA bar.
 *
 * Mounted in App.tsx (outside <Routes>) so it survives navigation and shows
 * on auth pages too — the whole point is letting QA see the same overlay
 * regardless of where they land.
 *
 * Renders only when `detectPreviewContext()` succeeds (preview hostname).
 * Hides itself otherwise — zero overhead for production / dev.elmes.io.
 */
import { useMemo, useState } from 'react';
import {
  detectPreviewContext,
  DEFAULT_ACCEPTANCE_CHECKLIST,
  type ParsedPreviewContext,
} from './previewContext.js';
import { usePreviewMeta } from './usePreviewMeta.js';
import { useConsoleErrors } from './useConsoleErrors.js';
import { MarkdownContent } from '../ai/MarkdownContent.js';
import { api } from '../../services/api.js';
import { useAuthStore } from '../../stores/authStore.js';

const HIDE_KEY = 'elmes:previewOverlay:hidden';

export function QAOverlay(): JSX.Element | null {
  const ctx = useMemo<ParsedPreviewContext | null>(() => detectPreviewContext(), []);
  const [hidden, setHidden] = useState<boolean>(
    () => typeof window !== 'undefined' && sessionStorage.getItem(HIDE_KEY) === '1',
  );
  const [expanded, setExpanded] = useState<boolean>(false);
  const [bodyExpanded, setBodyExpanded] = useState<boolean>(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [localChecks, setLocalChecks] = useState<Record<string, boolean>>({});

  const { meta, loading, refresh, setMeta } = usePreviewMeta(ctx?.name ?? null);
  const isAuthed = useAuthStore((s) => Boolean(s.accessToken));
  const errors = useConsoleErrors();

  if (!ctx) return null;

  const items = meta
    ? meta.acceptance
    : DEFAULT_ACCEPTANCE_CHECKLIST.map((i) => ({ ...i, passed: localChecks[i.id] ?? null }));

  const passedCount = items.filter((i) => i.passed === true).length;
  const totalCount = items.length;
  const allPassed = totalCount > 0 && passedCount === totalCount;

  const toggleCheck = async (checkId: string, current: boolean | null) => {
    // Cycle: null → true → false → null
    const next: boolean | null = current === null ? true : current ? false : null;

    if (!meta) {
      setLocalChecks((prev) => ({ ...prev, [checkId]: next ?? false }));
      return;
    }
    if (!isAuthed) {
       
      console.warn('[QAOverlay] cannot persist check: not authenticated');
      return;
    }
    setBusy(checkId);
    try {
      const res = await api.patch(`/preview/${meta.name}/checks/${checkId}`, {
        passed: next,
      });
      if (res.data?.success && res.data.data) setMeta(res.data.data);
    } catch (err) {
       
      console.warn('[QAOverlay] check update failed:', (err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const decide = async (decision: 'approved' | 'rejected') => {
    if (!meta) return;
    if (!isAuthed) {
       
      console.warn('[QAOverlay] cannot decide: login required');
      return;
    }
    setBusy(decision);
    try {
      const res = await api.post(`/preview/${meta.name}/decision`, { decision });
      if (res.data?.success && res.data.data) setMeta(res.data.data);
    } catch (err) {
       
      console.warn('[QAOverlay] decision failed:', (err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  if (hidden) {
    return (
      <button
        type="button"
        onClick={() => {
          sessionStorage.removeItem(HIDE_KEY);
          setHidden(false);
        }}
        title="Show preview QA overlay"
        className="fixed bottom-3 right-3 z-[9999] rounded-full bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white shadow-lg hover:bg-amber-600"
      >
        QA · {ctx.name}
      </button>
    );
  }

  const decisionPill = meta?.qaDecision
    ? meta.qaDecision === 'approved'
      ? <span className="rounded bg-emerald-600 px-2 py-0.5 text-white">QA approved</span>
      : <span className="rounded bg-red-600 px-2 py-0.5 text-white">QA rejected</span>
    : null;

  return (
    <div
      role="region"
      aria-label="QA preview overlay"
      className="fixed bottom-0 left-0 right-0 z-[9999] border-t border-amber-300 bg-amber-50/95 text-amber-900 shadow-[0_-2px_10px_rgba(0,0,0,0.08)] dark:border-amber-700 dark:bg-amber-900/95 dark:text-amber-100"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-1.5 text-xs">
        <span className="rounded bg-amber-500 px-2 py-0.5 font-semibold text-white">PREVIEW</span>
        <span className="font-mono">{ctx.name}</span>

        {ctx.kind === 'pr' && ctx.prNumber && (
          <a
            className="underline hover:no-underline"
            href={meta?.link?.prUrl ?? `https://github.com/serhanak/elmes-platform/pull/${ctx.prNumber}`}
            target="_blank"
            rel="noreferrer"
          >
            PR #{ctx.prNumber}
          </a>
        )}

        {meta?.link?.aiIssueId && (
          <a
            className="underline hover:no-underline"
            href={`/ai/issues/${meta.link.aiIssueId}`}
            title="Open AutoDev issue"
          >
            AI #{meta.link.aiIssueId}
          </a>
        )}

        {meta?.link?.roadmapItemId && (
          <a
            className="underline hover:no-underline"
            href={`/ai/roadmap/items/${meta.link.roadmapItemId}`}
            title={meta.link.roadmapItemTitle ?? 'Open roadmap item'}
          >
            Roadmap #{meta.link.roadmapItemId}
          </a>
        )}

        {meta?.branch && (
          <span className="hidden sm:inline">
            branch: <code className="font-mono">{meta.branch}</code>
          </span>
        )}

        {meta?.prTitle && (
          <span className="hidden truncate md:inline" title={meta.prTitle}>
            {meta.prTitle}
          </span>
        )}

        {meta?.spawnedAt && (
          <span className="hidden lg:inline" title={meta.spawnedAt}>
            spawned {timeAgo(meta.spawnedAt)}
          </span>
        )}

        {loading && <span className="text-amber-600">…</span>}
        {decisionPill}

        {errors.count > 0 && (
          <button
            type="button"
            onClick={() => {
              setExpanded(true);
              window.setTimeout(() => {
                document.getElementById('qa-overlay-errors')?.scrollIntoView({ behavior: 'smooth' });
              }, 50);
            }}
            className="rounded bg-red-500 px-2 py-0.5 font-semibold text-white"
            title="Click to expand error list"
          >
            ⚠ {errors.count} error{errors.count === 1 ? '' : 's'}
          </button>
        )}

        <span className="ml-auto flex flex-wrap items-center gap-2">
          <span
            className={`rounded px-2 py-0.5 ${
              allPassed
                ? 'bg-emerald-500 text-white'
                : 'bg-amber-200 text-amber-900 dark:bg-amber-700 dark:text-amber-50'
            }`}
          >
            {passedCount}/{totalCount} acceptance
          </span>

          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            className="rounded border border-amber-400 px-2 py-0.5 hover:bg-amber-100 dark:hover:bg-amber-800"
            aria-expanded={expanded}
          >
            {expanded ? 'Collapse ▾' : 'Acceptance ▴'}
          </button>

          <a
            href="/preview"
            title="Open preview admin (bundle picker, teardown, list)"
            className="rounded border border-amber-400 px-2 py-0.5 hover:bg-amber-100 dark:hover:bg-amber-800"
          >
            Admin
          </a>

          <button
            type="button"
            onClick={() => {
              refresh();
              window.location.reload();
            }}
            title="Reload"
            className="rounded border border-amber-400 px-2 py-0.5 hover:bg-amber-100 dark:hover:bg-amber-800"
          >
            ⟳
          </button>

          <button
            type="button"
            onClick={() => {
              sessionStorage.setItem(HIDE_KEY, '1');
              setHidden(true);
            }}
            title="Hide for this session"
            className="rounded border border-amber-400 px-2 py-0.5 hover:bg-amber-100 dark:hover:bg-amber-800"
          >
            ✕
          </button>
        </span>
      </div>

      {expanded && (
        <div className="border-t border-amber-300/60 px-3 py-2 dark:border-amber-700/60">
          <ul className="grid gap-1 text-sm sm:grid-cols-2">
            {items.map((item) => (
              <li key={item.id} className="flex items-start gap-2">
                <button
                  type="button"
                  onClick={() => toggleCheck(item.id, item.passed)}
                  disabled={busy === item.id}
                  title="Click to cycle: pending → pass → fail → pending"
                  className={`mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded border text-[11px] font-bold ${
                    item.passed === true
                      ? 'border-emerald-600 bg-emerald-500 text-white'
                      : item.passed === false
                      ? 'border-red-600 bg-red-500 text-white'
                      : 'border-amber-400 bg-white text-amber-700 dark:bg-amber-900'
                  }`}
                >
                  {item.passed === true ? '✓' : item.passed === false ? '✗' : '–'}
                </button>
                <span className={item.passed === false ? 'line-through opacity-70' : ''}>
                  {item.label}
                </span>
              </li>
            ))}
          </ul>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {meta && (
              <>
                <button
                  type="button"
                  onClick={() => decide('approved')}
                  disabled={busy !== null || !isAuthed || meta.qaDecision === 'approved'}
                  className="rounded bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() => decide('rejected')}
                  disabled={busy !== null || !isAuthed || meta.qaDecision === 'rejected'}
                  className="rounded bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                >
                  Reject
                </button>
              </>
            )}
            {!isAuthed && (
              <span className="text-[11px] text-amber-700 dark:text-amber-300">
                login to record decisions
              </span>
            )}
            {!meta && (
              <span className="text-[11px] text-amber-700 dark:text-amber-300">
                (offline — checklist is local only)
              </span>
            )}
          </div>

          {meta?.prBody && (
            <div className="mt-3 border-t border-amber-300/60 pt-2 dark:border-amber-700/60">
              <button
                type="button"
                onClick={() => setBodyExpanded((b) => !b)}
                aria-expanded={bodyExpanded}
                className="text-[11px] font-semibold underline-offset-2 hover:underline"
              >
                {bodyExpanded ? 'Hide PR description ▾' : 'Show PR description ▸'}
              </button>
              {bodyExpanded && (
                <div className="mt-2 max-h-72 overflow-y-auto rounded border border-amber-300/60 bg-white/70 p-2 text-[11px] leading-relaxed text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-100">
                  <MarkdownContent content={meta.prBody} />
                </div>
              )}
            </div>
          )}

          {errors.count > 0 && (
            <div
              id="qa-overlay-errors"
              className="mt-3 rounded border border-red-300 bg-red-50 p-2 text-[11px] text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100"
            >
              <div className="mb-1 flex items-center justify-between">
                <span className="font-semibold">{errors.count} JS error{errors.count === 1 ? '' : 's'} detected</span>
                <button
                  type="button"
                  onClick={errors.reset}
                  className="rounded border border-red-400 px-2 py-0.5 hover:bg-red-100 dark:hover:bg-red-900"
                >
                  Clear
                </button>
              </div>
              <ul className="max-h-40 space-y-0.5 overflow-y-auto font-mono">
                {errors.recent.slice(-10).reverse().map((r) => (
                  <li key={r.ts} className="truncate" title={`${r.source}: ${r.message}`}>
                    [{r.source}] {r.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function timeAgo(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const diffSec = Math.floor((Date.now() - t) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const m = Math.floor(diffSec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
